import { ChangeEvent, useRef, useState } from 'react';
import { Checkbox, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { revokeRegistrationBusinessCard, scanRegistrationBusinessCard } from '@/api/auth.api';
import { Button } from '@/components/UI/Button';
import { errorHandler } from '@/utils/toastHandler';
import { legacyBusinessCardFieldsToCandidates } from '../businessCard';

import {
  CandidateField,
  CandidateReview,
  FileName,
  HiddenFileInput,
  RemoveButton,
  ReviewActions,
  SourceText,
  UploadDescription,
  UploadRow,
  UploadSection,
} from './styles';

const MAX_FILE_SIZE = 100 * 1024 * 1024;
const SUPPORTED_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'bmp', 'gif', 'tif', 'tiff', 'webp', 'pdf']);
const FIELD_KEYS: Array<keyof BusinessCardCandidates> = [
  'acc_coname', 'acc_firstName', 'acc_lastName', 'acc_jobPosition', 'acc_mobile',
  'com_email', 'acc_tel', 'acc_fax', 'acc_address', 'acc_website',
];

interface BusinessCardUploadProps {
  existingValues?: BusinessCardSelectedValues;
  onApply: (values: BusinessCardSelectedValues) => void;
  onUploadTokenChange?: (token?: string) => void;
}

interface BusinessCardUploadErrorData {
  business_card_upload_token?: string;
}

interface BusinessCardUploadErrorResponse {
  message?: string;
  data?: BusinessCardUploadErrorData;
}

const getBusinessCardUploadErrorResponse = (error: unknown): BusinessCardUploadErrorResponse | undefined => {
  if (!error || typeof error !== 'object' || !('response' in error)) return undefined;
  const response = (error as { response?: { data?: unknown } }).response;
  const data = response?.data;
  return data && typeof data === 'object' ? data as BusinessCardUploadErrorResponse : undefined;
};

const BusinessCardUpload = ({ existingValues = {}, onApply, onUploadTokenChange = () => {} }: BusinessCardUploadProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [candidates, setCandidates] = useState<BusinessCardCandidates | null>(null);
  const [selectedIds, setSelectedIds] = useState<Partial<Record<keyof BusinessCardCandidates, string>>>({});
  const [replaceFields, setReplaceFields] = useState<Partial<Record<keyof BusinessCardCandidates, boolean>>>({});
  const [uploadToken, setUploadToken] = useState<string>();

  const resetFileState = () => {
    setFile(null);
    setCandidates(null);
    setSelectedIds({});
    setReplaceFields({});
    if (inputRef.current) inputRef.current.value = '';
  };

  const revokeUpload = async () => {
    if (uploadToken) {
      try {
        await revokeRegistrationBusinessCard(uploadToken);
      } catch {
        // The pending upload expires server-side; clearing local state still prevents reuse.
      }
    }
    setUploadToken(undefined);
    onUploadTokenChange(undefined);
    resetFileState();
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;
    const extension = selectedFile.name.split('.').pop()?.toLowerCase() || '';
    if (!SUPPORTED_EXTENSIONS.has(extension) || selectedFile.size > MAX_FILE_SIZE) {
      errorHandler(t('pages.auth.businessCard.unsupportedFile'));
      await revokeUpload();
      return;
    }

    if (uploadToken) {
      try {
        await revokeRegistrationBusinessCard(uploadToken);
      } catch {
        // The server-side expiry job is the fallback for an already missing upload.
      }
      setUploadToken(undefined);
      onUploadTokenChange(undefined);
    }

    setFile(selectedFile);
    setIsScanning(true);
    setCandidates(null);
    try {
      const response = (await scanRegistrationBusinessCard(selectedFile)).data;
      if (!response.success) throw new Error(response.message);
      setUploadToken(response.data.business_card_upload_token);
      onUploadTokenChange(response.data.business_card_upload_token);
      const extractedCandidates = response.data.candidates
        || legacyBusinessCardFieldsToCandidates(response.data.fields);
      setCandidates(extractedCandidates);
      const defaults: Partial<Record<keyof BusinessCardCandidates, string>> = {};
      FIELD_KEYS.forEach((field) => {
        if (extractedCandidates[field]?.length === 1) defaults[field] = extractedCandidates[field][0].id;
      });
      setSelectedIds(defaults);
    } catch (error: unknown) {
      const errorResponse = getBusinessCardUploadErrorResponse(error);
      const retainedToken = errorResponse?.data?.business_card_upload_token;
      if (retainedToken) {
        setUploadToken(retainedToken);
        onUploadTokenChange(retainedToken);
      }
      errorHandler(errorResponse?.message || t('pages.auth.businessCard.scanFailed'));
    } finally {
      setIsScanning(false);
    }
  };

  const applySelection = () => {
    if (!candidates) return;
    const values: BusinessCardSelectedValues = {};
    FIELD_KEYS.forEach((field) => {
      if (existingValues[field]?.trim() && !replaceFields[field]) return;
      const selected = candidates[field].find((candidate) => candidate.id === selectedIds[field]);
      if (selected) values[field] = selected.value;
    });
    onApply(values);
    setCandidates(null);
  };

  const hasCandidates = candidates && FIELD_KEYS.some((field) => candidates[field].length > 0);

  return (
    <UploadSection>
      <UploadDescription>{t('pages.auth.businessCard.description')}</UploadDescription>
      <UploadRow>
        <FileName title={file?.name}>{file?.name || t('pages.auth.businessCard.noFile')}</FileName>
        <div>
          <HiddenFileInput ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.bmp,.gif,.tif,.tiff,.webp,.pdf"
            aria-label={t('pages.auth.businessCard.upload')} onChange={handleFileChange} />
          <Button type="button" size="table" variant="primary" disabled={isScanning}
            onClick={() => inputRef.current?.click()}>
            {isScanning ? t('pages.auth.businessCard.scanning') : t('pages.auth.businessCard.upload')}
          </Button>
          {file && !isScanning && <RemoveButton type="button" aria-label={t('pages.auth.businessCard.remove')}
            onClick={() => void revokeUpload()}>{t('pages.auth.businessCard.remove')}</RemoveButton>}
        </div>
      </UploadRow>

      {hasCandidates && <CandidateReview aria-label={t('pages.auth.businessCard.reviewTitle')}>
        <UploadDescription>{t('pages.auth.businessCard.reviewDescription')}</UploadDescription>
        {FIELD_KEYS.map((field) => candidates[field].length > 0 && <CandidateField key={field}>
          <legend>{t(`pages.auth.businessCard.fields.${field}`)}</legend>
          <RadioGroup value={selectedIds[field] || ''}
            onChange={(event) => setSelectedIds((current) => ({ ...current, [field]: event.target.value }))}>
            {candidates[field].map((candidate) => <FormControlLabel key={candidate.id} value={candidate.id}
              control={<Radio />} label={<span>{candidate.value}<SourceText>{candidate.sourceText}</SourceText></span>} />)}
          </RadioGroup>
          {Boolean(existingValues[field]?.trim()) && <FormControlLabel
            control={<Checkbox checked={Boolean(replaceFields[field])}
              onChange={(event) => setReplaceFields((current) => ({ ...current, [field]: event.target.checked }))} />}
            label={t('pages.auth.businessCard.replaceExisting')} />}
        </CandidateField>)}
        <ReviewActions>
          <Button type="button" size="table" variant="primary" onClick={applySelection}>
            {t('pages.auth.businessCard.applySelected')}
          </Button>
          <Button type="button" size="table" variant="primary-invert" onClick={() => setCandidates(null)}>
            {t('buttons.cancel')}
          </Button>
        </ReviewActions>
      </CandidateReview>}
      {candidates && !hasCandidates && <UploadDescription>{t('pages.auth.businessCard.noCandidates')}</UploadDescription>}
    </UploadSection>
  );
};

export default BusinessCardUpload;
